/**
 * Config › Schedules — an AI Service's own triggers (task 1003).
 *
 * Report: `project/reports/innovate-service-endpoint-schedules.md`.
 * Design: `project/artifacts/innovate-service-detail-schedules-mockup.html`.
 *
 * The fifth Config section, after Access, and an OWN-SAVE surface like it: a
 * schedule is configuration of the service — the same kind of thing as who may
 * call it — but it is not part of the collaboration config, so the Config
 * panel's Edit / Save / Cancel row steps aside and each schedule saves through
 * its own form.
 *
 * One list, one inline form (`ScheduleForm` replaces the list while open), and
 * per-row Pause / Resume / Edit / Delete. A row expands to its recent
 * occurrences, read from the schedule's own run log — which is where skipped
 * occurrences live, since a skip produced no call and so has no business in the
 * AI Service's run history.
 *
 * **The three plan states are the existing components, never new copy**
 * (mockup §F): `PlanRequiredNotice` when the Account has no plan,
 * `UpgradePrompt` for the `max_schedules` limit and for an unentitled plan. The
 * section renders on every plan — kv 2026-09-24: show it and say why, rather
 * than hide a capability a User is paying to discover.
 *
 * The quota reads the ACCOUNT's active-schedule count, not this service's,
 * because `max_schedules` is an account cap and the service counts every active
 * row the Account holds when it judges the next create.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ClockIcon, PlusIcon } from '@heroicons/react/24/outline';
import DeleteResourceControl from '@/components/shared/DeleteResourceControl';
import { EmptyState } from '@/components/shared/EmptyState';
import PlanRequiredNotice from '@/components/ui/PlanRequiredNotice';
import UpgradePrompt from '@/components/ui/UpgradePrompt';
import { useAccountPlan } from '@/hooks/useAccountUsage';
import { useEnvironments } from '@/hooks/useEnvironments';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useRequiresPlan } from '@/hooks/useRequiresPlan';
import {
  useActiveScheduleCount,
  useDeleteSchedule,
  useRunLinksForRequests,
  useSchedules,
  useScheduleRunLogs,
  useUpdateSchedule,
} from '@/hooks/useSchedules';
import { useOptionalToast } from '@/contexts/ToastContext';
import { describeCron } from '@/lib/scheduleCron';
import { formatRelativeTime } from '@/utils/dateUtils';
import { scheduleRunDetailText, scheduleStatusDetailText } from '@/utils/spending';
import type { Endpoint } from '@/types';
import {
  SCHEDULE_RUN_STATE_LABELS,
  SCHEDULE_STATUS_LABELS,
  type Schedule,
  type ScheduleRun,
  type ScheduleStatus,
} from '@/types/schedules';
import { ScheduleForm } from './ScheduleForm';
import { RESUME_SERVICE_FIRST } from './serviceStop';
import { serviceRunPath } from '@/components/runExplorer/runExplorerLinks';

/** The plan limit keys this section reads. Names, never values. */
const MAX_SCHEDULES_LIMIT_KEY = 'max_schedules';
const MIN_INTERVAL_LIMIT_KEY = 'min_schedule_interval_seconds';

const STATUS_CHIP_CLASSES: Record<ScheduleStatus, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  paused: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  failed: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
};

const RUN_STATE_CHIP_CLASSES: Record<ScheduleRun['state'], string> = {
  fired: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  skipped_overlap: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  skipped_paused: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
};

/** "in 19 hours" — the future half of `formatRelativeTime`, which looks back. */
const formatRelativeFuture = (instant: string): string => {
  const seconds = Math.floor((new Date(instant).getTime() - Date.now()) / 1000);
  if (seconds <= 0) return 'due now';
  if (seconds < 60) return 'in under a minute';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `in ${days} day${days === 1 ? '' : 's'}`;
};

/** An instant as the viewer's own browser reads it. */
const formatLocal = (instant: string): string =>
  new Date(instant).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

interface ServiceSchedulesSectionProps {
  endpoint: Endpoint;
}

export function ServiceSchedulesSection({ endpoint }: ServiceSchedulesSectionProps) {
  const toast = useOptionalToast();
  const deleteEnabled = useFlag('innovate.fe.schedules.delete');
  const { schedules, isLoading } = useSchedules(endpoint.id);
  const runLogs = useScheduleRunLogs(schedules);
  /**
   * The run detail page is addressed by COLLABORATION id and a log row names
   * the REQUEST, so the ids on screen are resolved to their runs here — once,
   * for every row — rather than per link.
   */
  const runLinks = useRunLinksForRequests(
    [...runLogs.values()].flatMap((rows) =>
      rows.map((row) => row.endpointRequestId).filter((id): id is string => !!id)
    )
  );
  const { activeCount } = useActiveScheduleCount();
  const { limits, isResolved } = useAccountPlan();
  const requiresPlan = useRequiresPlan();
  const { environments } = useEnvironments();
  const updateSchedule = useUpdateSchedule();
  const deleteSchedule = useDeleteSchedule();

  const [editing, setEditing] = useState<{ schedule?: Schedule } | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  /** A refusal the User has to see before the action they asked for happens. */
  const [limitRefusal, setLimitRefusal] = useState<{ current: number; cap: number } | null>(null);

  const cap = isResolved ? limits[MAX_SCHEDULES_LIMIT_KEY] : undefined;
  const minIntervalSeconds = isResolved ? limits[MIN_INTERVAL_LIMIT_KEY] ?? null : null;
  /**
   * A plan that carries the feature carries a non-zero cap, and a plan without
   * it carries 0 — the two halves move together in the AMS seed, which is what
   * lets this say "not included in your plan" instead of waiting for a refusal.
   */
  const featureOnPlan = cap === undefined || cap !== 0;
  const capReached = typeof cap === 'number' && cap > 0 && (activeCount ?? 0) >= cap;

  const quotaLabel = () => {
    if (typeof cap !== 'number' || activeCount === null) return null;
    if (cap < 0) return `${activeCount} schedule${activeCount === 1 ? '' : 's'}`;
    return `${activeCount} of ${cap} schedules`;
  };

  const environmentName = (environmentId: string | null): string | null =>
    environmentId ? environments.find((row) => row.id === environmentId)?.name ?? null : null;

  /** The rule in words, with the zone (and stage) it is read in underneath. */
  const whenSentence = (schedule: Schedule): string => {
    if (schedule.kind === 'once') {
      return schedule.runAt ? formatLocal(schedule.runAt) : 'Once';
    }
    return schedule.cron ? describeCron(schedule.cron) : 'Recurring';
  };

  const openForm = (schedule?: Schedule) => {
    setLimitRefusal(null);
    if (!schedule && capReached) {
      setLimitRefusal({ current: activeCount ?? 0, cap: cap as number });
      return;
    }
    setEditing({ schedule });
  };

  const setStatus = async (schedule: Schedule, status: 'active' | 'paused') => {
    if (status === 'active' && capReached) {
      setLimitRefusal({ current: activeCount ?? 0, cap: cap as number });
      return;
    }
    try {
      await updateSchedule.mutateAsync({ id: schedule.id, data: { status } });
      toast?.success(status === 'paused' ? 'Schedule paused' : 'Schedule resumed');
    } catch (error) {
      const refusal = error as Error & { status?: number };
      // 402/403 are already on screen through the EntitlementGuard.
      if (refusal.status !== 402 && refusal.status !== 403) {
        toast?.error('Could not change the schedule', refusal.message);
      }
    }
  };

  if (editing) {
    return (
      <ScheduleForm
        endpoint={endpoint}
        schedule={editing.schedule}
        minIntervalSeconds={minIntervalSeconds}
        onClose={() => setEditing(null)}
      />
    );
  }

  return (
    <section
      data-testid="service-schedules-section"
      className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="flex items-center text-sm font-semibold text-gray-900 dark:text-white">
            <ClockIcon className="mr-2 h-5 w-5 text-gray-400" aria-hidden="true" />
            Schedules
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Run this AI Service on a recurring rule or once at a set time. Each scheduled run
            appears under Testing like any other call.
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-3">
          {quotaLabel() && (
            <span
              data-testid="schedules-quota"
              title="Counts every active schedule in this account, which is what your plan caps."
              className="text-xs text-gray-500 dark:text-gray-400"
            >
              {quotaLabel()}
            </span>
          )}
          {featureOnPlan && !requiresPlan && (
            <button
              type="button"
              onClick={() => openForm()}
              data-testid="schedules-add"
              className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
              Add schedule
            </button>
          )}
          {requiresPlan && <PlanRequiredNotice />}
        </div>
      </div>

      {/* An Account with no plan keeps its list, read-only: the rows are still
          the truth about what would fire once a plan is active. */}
      {requiresPlan && (
        <p
          data-testid="schedules-no-plan-note"
          className="mt-4 text-sm text-gray-600 dark:text-gray-300"
        >
          Schedules run on your plan's call allowance. Existing schedules stay listed but will not
          fire until a plan is active.
        </p>
      )}

      {/* Not on this plan at all. The section still renders (kv 2026-09-24) and
          the upgrade prompt takes the Add button's place. */}
      {!featureOnPlan && (
        <div className="mt-4" data-testid="schedules-feature-prompt">
          <UpgradePrompt
            error={{ error: 'feature_not_entitled', feature: 'innovate.schedules' }}
            onDismiss={() => undefined}
          />
        </div>
      )}

      {/* The cap, refused at the point of intent — clicking Add, or Resume on a
          paused row — with the same prompt the service's own 402 renders. */}
      {limitRefusal && (
        <div className="mt-4" data-testid="schedules-limit-prompt">
          <UpgradePrompt
            error={{
              error: 'limit_exceeded',
              limit: MAX_SCHEDULES_LIMIT_KEY,
              current: limitRefusal.current,
              cap: limitRefusal.cap,
            }}
            onDismiss={() => setLimitRefusal(null)}
          />
        </div>
      )}

      {isLoading ? (
        <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">Loading schedules…</p>
      ) : schedules.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon={<ClockIcon className="h-12 w-12" />}
            title="No schedules yet"
            description="Add a schedule to run this AI Service every morning, on the first of the month, or once at a time you choose. The run uses a saved input and counts toward your plan like an API call."
            action={
              featureOnPlan && !requiresPlan
                ? { label: 'Add schedule', onClick: () => openForm(), variant: 'primary' }
                : undefined
            }
          />
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="py-2 pr-4">Schedule</th>
                <th className="py-2 pr-4">When</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Last run</th>
                <th className="py-2 pr-4">Next run</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm dark:divide-gray-700">
              {schedules.map((schedule) => {
                const runs = runLogs.get(schedule.id) ?? [];
                const lastRun = runs.find((row) => row.endpointRequestId) ?? null;
                const stage = environmentName(schedule.environmentId);
                const expanded = expandedId === schedule.id;

                return (
                  <ScheduleRow
                    key={schedule.id}
                    schedule={schedule}
                    runs={runs}
                    lastRun={lastRun}
                    stage={stage}
                    expanded={expanded}
                    runLinks={runLinks}
                    whenSentence={whenSentence(schedule)}
                    deleteEnabled={deleteEnabled}
                    endpointId={endpoint.id}
                    serviceStopped={!!endpoint.stop}
                    onToggleOccurrences={() => setExpandedId(expanded ? null : schedule.id)}
                    onEdit={() => openForm(schedule)}
                    onPause={() => setStatus(schedule, 'paused')}
                    onResume={() => setStatus(schedule, 'active')}
                    onDelete={() => deleteSchedule.mutateAsync(schedule.id)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

interface ScheduleRowProps {
  schedule: Schedule;
  runs: ScheduleRun[];
  lastRun: ScheduleRun | null;
  stage: string | null;
  expanded: boolean;
  /** `endpointRequestId` → collaboration id, for the rows that became a call. */
  runLinks: Map<string, string>;
  whenSentence: string;
  deleteEnabled: boolean;
  endpointId: string;
  /**
   * The AI Service has an open stop (task 1164). Resuming a Schedule would only
   * meet the stop gate again, so the row's Resume waits until the service is
   * started again (the tooltip says "Start the AI Service first", task 1191).
   */
  serviceStopped: boolean;
  onToggleOccurrences: () => void;
  onEdit: () => void;
  onPause: () => void;
  onResume: () => void;
  onDelete: () => Promise<unknown>;
}

function ScheduleRow({
  schedule,
  runs,
  lastRun,
  stage,
  expanded,
  runLinks,
  whenSentence,
  deleteEnabled,
  endpointId,
  serviceStopped,
  onToggleOccurrences,
  onEdit,
  onPause,
  onResume,
  onDelete,
}: ScheduleRowProps) {
  // A run refused by the spending limit reads as the role-aware explanation
  // rather than its raw code (task 1159): owners and admins are pointed at
  // Settings → Billing → Spending, members at the account owner. The wording
  // follows the spending limit's own lever (task 1217).
  const canManage = useFlag('innovate.fe.account.spending');
  const runLink = (run: ScheduleRun): string | null => {
    const collaborationId = run.endpointRequestId
      ? runLinks.get(run.endpointRequestId)
      : undefined;
    return collaborationId ? serviceRunPath(endpointId, collaborationId) : null;
  };

  return (
    <>
      <tr data-testid={`schedule-row-${schedule.id}`} className="align-top">
        <td className="py-3 pr-4">
          <p className="font-medium text-gray-900 dark:text-white">{schedule.name}</p>
          {schedule.description && (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {schedule.description}
            </p>
          )}
          <button
            type="button"
            onClick={onToggleOccurrences}
            data-testid={`schedule-occurrences-toggle-${schedule.id}`}
            className="mt-1 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
          >
            {expanded ? 'Hide occurrences' : 'Recent occurrences'}
          </button>
        </td>

        <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">
          <p>{whenSentence}</p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {schedule.timezone ?? 'UTC'}
            {stage ? ` · ${stage}` : ''}
          </p>
          <p className="mt-0.5 font-mono text-xs text-gray-400 dark:text-gray-500">
            {schedule.kind === 'once' ? 'once' : schedule.cron}
          </p>
        </td>

        <td className="py-3 pr-4">
          <span
            data-testid={`schedule-status-${schedule.id}`}
            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CHIP_CLASSES[schedule.status]}`}
          >
            {SCHEDULE_STATUS_LABELS[schedule.status]}
          </span>
          {schedule.statusDetail && (
            <p
              data-testid={`schedule-status-detail-${schedule.id}`}
              className="mt-1 max-w-xs text-xs text-gray-500 dark:text-gray-400"
            >
              {scheduleStatusDetailText(schedule.statusDetail)}
            </p>
          )}
        </td>

        <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">
          {schedule.lastRunAt ? (
            <>
              {lastRun && runLink(lastRun) ? (
                <Link
                  to={runLink(lastRun)!}
                  state={{ scheduleId: schedule.id, scheduleName: schedule.name }}
                  className="text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  {formatRelativeTime(schedule.lastRunAt)}
                </Link>
              ) : (
                <span>{formatRelativeTime(schedule.lastRunAt)}</span>
              )}
              {lastRun && (
                <span
                  className={`ml-2 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${RUN_STATE_CHIP_CLASSES[lastRun.state]}`}
                >
                  {SCHEDULE_RUN_STATE_LABELS[lastRun.state]}
                </span>
              )}
            </>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">never run</span>
          )}
        </td>

        <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">
          {schedule.nextRunAt && schedule.status === 'active' ? (
            <>
              <p>{formatLocal(schedule.nextRunAt)}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatRelativeFuture(schedule.nextRunAt)}
              </p>
            </>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">—</span>
          )}
        </td>

        <td className="py-3">
          <div className="flex items-center justify-end gap-2">
            {schedule.status === 'active' && (
              <button
                type="button"
                onClick={onPause}
                data-testid={`schedule-pause-${schedule.id}`}
                className="text-xs font-medium text-gray-600 hover:underline dark:text-gray-300"
              >
                Pause
              </button>
            )}
            {(schedule.status === 'paused' || schedule.status === 'failed') && (
              <button
                type="button"
                onClick={onResume}
                disabled={serviceStopped}
                title={serviceStopped ? RESUME_SERVICE_FIRST : undefined}
                data-testid={`schedule-resume-${schedule.id}`}
                className="text-xs font-medium text-gray-600 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline dark:text-gray-300 dark:disabled:text-gray-500"
              >
                Resume
              </button>
            )}
            {schedule.status !== 'completed' && (
              <button
                type="button"
                onClick={onEdit}
                data-testid={`schedule-edit-${schedule.id}`}
                className="text-xs font-medium text-gray-600 hover:underline dark:text-gray-300"
              >
                Edit
              </button>
            )}
            {deleteEnabled && (
              <DeleteResourceControl
                flag="innovate.fe.schedules.delete"
                entityLabel="schedule"
                name={schedule.name}
                id={schedule.id}
                onDelete={onDelete}
                testId={`schedule-delete-${schedule.id}`}
                body={
                  <>
                    The schedule stops firing. Runs it already produced stay in this AI Service's
                    run history.
                  </>
                }
              />
            )}
          </div>
        </td>
      </tr>

      {expanded && (
        <tr data-testid={`schedule-occurrences-${schedule.id}`}>
          <td colSpan={6} className="pb-4">
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Recent occurrences
              </p>
              {runs.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  No occurrences yet. They appear here as the schedule fires — including the ones
                  skipped because the previous run was still going.
                </p>
              ) : (
                <ul className="mt-2 space-y-1">
                  {runs.map((run) => (
                    <li
                      key={run.id}
                      className="flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-300"
                    >
                      <span className="font-mono">{formatLocal(run.scheduledFor)}</span>
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 font-medium ${RUN_STATE_CHIP_CLASSES[run.state]}`}
                      >
                        {SCHEDULE_RUN_STATE_LABELS[run.state]}
                      </span>
                      {run.detail && <span data-testid={`schedule-run-detail-${run.id}`}>{scheduleRunDetailText(run.detail, canManage)}</span>}
                      {runLink(run) && (
                        <Link
                          to={runLink(run)!}
                          state={{ scheduleId: schedule.id, scheduleName: schedule.name }}
                          className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                        >
                          View run
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default ServiceSchedulesSection;
